import { useMemo, useState } from 'react';
import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionProps,
  AccordionSummary as MuiAccordionSummary,
  AccordionSummaryProps,
  Alert,
  Box,
  Button,
  Divider,
  Stack,
  styled,
  Typography,
} from '@mui/material';

import { AccessGroupClientModel, AccessRequestClientModel } from '@client-app-sdk';

import AccessGroupSummary from '@common/components/AccessGroupSummary';
import AccessGroupDetails from '@common/organisms/AccessGroupDetails';
import { useLocation } from 'react-router-dom';
import routes from '@routes';
import { AnalyticsEvents } from '@common/utils/analytics/events';
import { useAnalyticsContext } from '@common/utils/analytics/Provider';
import AccessGroupUnits from '@common/organisms/AccessGroupUnits';
import { MaterialIcon } from '@common/ui/MaterialIcon';

const Accordion = styled((props: AccordionProps) => <MuiAccordion elevation={0} {...props} />)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  overflow: 'hidden',

  '&:before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => <MuiAccordionSummary {...props} />)(({ theme }) => ({
  backgroundColor: theme.palette.background.paper,
  padding: 0,

  '&.MuiAccordionSummary-root': {
    '&.Mui-expanded': {
      minHeight: 'auto',
    },
  },

  '& .MuiAccordionSummary-content': {
    display: 'block',
    margin: 0,
    padding: theme.spacing(1.5),

    '&.Mui-expanded': {
      margin: 0,
    },
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

export default function AccessGroups({
  groups,
  canSeeDetails,
  onDetailsExpand,
  initNewCreds,
  isMyAccessEnabled,
}: {
  groups: AccessRequestClientModel['access_groups'];
  canSeeDetails: boolean;
  onDetailsExpand?: (group: AccessGroupClientModel) => void;
  initNewCreds?: boolean;
  isMyAccessEnabled?: boolean;
}) {
  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Access to:</Typography>
      <Box>
        {groups.map((ag) => (
          <AccessGroup
            key={ag.id}
            group={ag}
            canSeeDetails={canSeeDetails}
            onDetailsExpand={onDetailsExpand}
            initNewCreds={initNewCreds}
            isMyAccessEnabled={isMyAccessEnabled}
          />
        ))}
      </Box>
    </Stack>
  );
}

function AccessGroup({
  group,
  canSeeDetails,
  onDetailsExpand,
  initNewCreds,
  isMyAccessEnabled,
}: {
  group: AccessGroupClientModel;
  canSeeDetails: boolean;
  onDetailsExpand?: (group: AccessGroupClientModel) => void;
  initNewCreds?: boolean;
  isMyAccessEnabled?: boolean;
}) {
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [accessUnitsExpanded, setAccessUnitsExpanded] = useState(false);
  const { track } = useAnalyticsContext();
  const { pathname } = useLocation();

  const pathToUnitsEventMap = useMemo(
    () => ({
      [routes.OpenRequests.path]: AnalyticsEvents.OpenRequestAccessUnitsClicked,
      [routes.ClosedRequests.path]: AnalyticsEvents.ClosedRequestAccessUnitsClicked,
      [routes.Tasks.path]: AnalyticsEvents.TaskAccessUnitsClicked,
    }),
    [],
  );

  const handleDetailsExpandClick = () => {
    setDetailsExpanded((prev) => !prev);

    if (canSeeDetails) onDetailsExpand?.(group);

    if (pathname in pathToUnitsEventMap) {
      track(pathToUnitsEventMap[pathname]);
    }
  };

  const handleAccessUnitsExpandClick = () => {
    setAccessUnitsExpanded((prev) => !prev);
  };

  return (
    <Accordion expanded={detailsExpanded} onChange={handleDetailsExpandClick}>
      <AccordionSummary>
        {group.status?.message && (
          <Alert variant="outlined" severity="warning" sx={{ mb: 2 }}>
            {group.status?.message}
          </Alert>
        )}
        <AccessGroupSummary
          group={group}
          initNewCreds={initNewCreds}
          canSeeDetails={canSeeDetails}
          detailsActive={detailsExpanded}
          isMyAccessEnabled={isMyAccessEnabled}
        />
      </AccordionSummary>
      <AccordionDetails>
        {isMyAccessEnabled ? (
          <AccessGroupUnits group={group} />
        ) : (
          <Stack spacing={1}>
            {canSeeDetails && detailsExpanded && (
              <AccessGroupDetails group={group} onReset={() => setDetailsExpanded(false)} />
            )}
            {canSeeDetails && (
              <Divider>
                <Button
                  size="small"
                  onClick={handleAccessUnitsExpandClick}
                  startIcon={<MaterialIcon symbol={accessUnitsExpanded ? 'expand_more' : 'chevron_right'} />}
                >
                  {accessUnitsExpanded ? 'Hide' : 'View'} resources
                </Button>
              </Divider>
            )}
            {(accessUnitsExpanded || !canSeeDetails) && <AccessGroupUnits group={group} />}
          </Stack>
        )}
      </AccordionDetails>
    </Accordion>
  );
}
